import {
  Button,
  Checkbox,
  Form,
  FormRule,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
} from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ZAttrMeta, zAttrMeta } from "src/business/attribute";
import { rxAccept, ZAttrComponent } from "src/business/attribute/attrEdits";
import { SelectCatalogId } from "src/business/catalog/SelectCatalog";
import { SelectValueCatalogId } from "src/business/valueCatalog/SelectValueCatalog";
import { cvtError, onError } from "src/common/onError";
import { parseRegExp } from "src/common/parseRegExp";
import { maxNameLength, rxName, rxNameMsg } from "src/common/rxName";
import { trimRequired } from "src/common/validationRules";
import { DatePickerString } from "src/components/DatePickerString";
import { IdBox } from "src/components/IdBox";
import { MdmDrawer } from "src/components/MdmDrawer";
import { msgQueueStore } from "src/components/MsgQueue";

import {
  AttrEditorData,
  AttrEditorField,
  boolOptions,
  isFieldVisible,
} from "./AttrEditorField";
import * as styles from "./AttrEditorV2.module.less";
import { AttrEditorV2Store } from "./AttrEditorV2Store";
import { OptionsEditor } from "./OptionsEditor";
import { SelectAttrForValueCatalog } from "./SelectAttrForValueCatalog";

type PropsAttrEditorV2 = {
  store: AttrEditorV2Store | null;
  close(): void;
  onOk(newAttr: ZAttrMeta): void;
};

const f = (id: AttrEditorField): string => id;

export const AttrEditorV2: React.FC<PropsAttrEditorV2> = observer((props) => {
  const { store, close, onOk } = props;
  const isNew = store?.ctx.isNew ?? true;
  const title = store?.title;
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (store) {
      form.resetFields();
      form.setFieldsValue(store.getInitialValues());
      store.onChange(form.getFieldsValue());
    }
  }, [store]);
  const attrType = store?.attrType;

  const onFinish = (values: AttrEditorData) => {
    try {
      const result = store!.makeResult(values);
      onOk(zAttrMeta.parse(result));
    } catch (e) {
      onError(e);
    }
  };
  const onValuesChange = (_: unknown, values: AttrEditorData) => {
    store?.onChange(values);
  };
  const noChanges = !store?.changed;
  const srcAttr = store?.srcAttr;
  const textPattern = Form.useWatch(f("textPattern"), form);
  const dateOnly = Form.useWatch(f("dateOnly"), form);

  return (
    <MdmDrawer
      wide={false}
      open={!!store}
      className={styles.drawer}
      title={title}
      onClose={close}
      extra={
        <Space>
          <Button
            type="primary"
            onClick={() => form.submit()}
            disabled={noChanges}
            title={noChanges ? "Нет изменений" : undefined}
          >
            Применить
          </Button>
        </Space>
      }
    >
      <div className={styles.contentBox}>
        {!!store && (
          <Form<AttrEditorData>
            form={form}
            layout="vertical"
            className={styles.form}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            onFinishFailed={() =>
              msgQueueStore.add({
                type: "error",
                message: "Остались ошибки в полях",
              })
            }
          >
            <Form.Item label="Тип атрибута">
              <Select
                disabled
                value={attrType?.key}
                options={[
                  {
                    value: attrType?.key,
                    label: attrType ? (
                      <Space align="center">
                        {attrType.icon}
                        <span>{attrType.name}</span>
                      </Space>
                    ) : null,
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="name"
              label="Системное название"
              rules={[
                trimRequired(),
                { pattern: rxName, message: rxNameMsg() },
                { max: maxNameLength },
                uniqueName(store.ctx.usedNames),
              ]}
            >
              <Input allowClear disabled={!isNew} maxLength={maxNameLength} />
            </Form.Item>

            {!isNew && (
              <IdBox
                entity={{
                  id: store.srcAttr.uid,
                  displayId: store.srcAttr.displayId,
                }}
              />
            )}

            <Form.Item
              name="title"
              label="Отображаемое название"
              rules={[trimRequired(), { max: maxNameLength }]}
            >
              <Input allowClear maxLength={maxNameLength} />
            </Form.Item>

            {isFieldVisible("required", srcAttr) && (
              <Form.Item noStyle name={f("required")} valuePropName="checked">
                <Checkbox>Обязательно к заполнению</Checkbox>
              </Form.Item>
            )}

            <div className={styles.col3}>
              <Form.Item name={f("order")} label="Ранг" tooltip={msgOrder}>
                <InputNumber />
              </Form.Item>
            </div>

            {srcAttr?.type.name === "string" && (
              <>
                <Form.Item name={f("textComponent")} label="Компонент">
                  <Select
                    options={["Input", "TextArea"].map((value) => ({
                      value,
                      label: value,
                    }))}
                    allowClear
                  />
                </Form.Item>

                <div className={styles.col3}>
                  <Form.Item
                    name={f("textMaxLength")}
                    label="Максимальная длина"
                  >
                    <InputNumber precision={0} min={1} />
                  </Form.Item>
                </div>

                <Form.Item
                  name={f("textDefaultValue")}
                  label={labelDefault}
                  tooltip={msgDefault}
                >
                  <Input allowClear />
                </Form.Item>

                <Form.Item
                  name={f("textPattern")}
                  label="Шаблон"
                  tooltip={msgPattern}
                  rules={[testPattern]}
                >
                  <Input allowClear />
                </Form.Item>
                {!!textPattern && (
                  <Form.Item name={f("textPatternMsg")}>
                    <Input
                      placeholder="Сообщение об ошибке шаблона"
                      allowClear
                    />
                  </Form.Item>
                )}
              </>
            )}

            {srcAttr?.type.name === "number" && (
              <>
                <div className={styles.col3}>
                  {isFieldVisible("numberPrecision", srcAttr) && (
                    <Form.Item
                      name={f("numberPrecision")}
                      tooltip="Количество знаков после запятой"
                      label="Точность"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={1} max={10} precision={0} />
                    </Form.Item>
                  )}
                  <Form.Item name={f("numberMin")} label="Минимальное значение">
                    <InputNumber />
                  </Form.Item>
                  <Form.Item
                    name={f("numberMax")}
                    label="Максимальное значение"
                    dependencies={[f("numberMin")]}
                    rules={[checkMax]}
                  >
                    <InputNumber />
                  </Form.Item>
                </div>
                {isFieldVisible("numberDefaultValue", srcAttr) && (
                  <Form.Item
                    name={f("numberDefaultValue")}
                    label={`${labelDefault}.num`}
                    tooltip={msgDefault}
                  >
                    <InputNumber />
                  </Form.Item>
                )}
              </>
            )}

            {isFieldVisible("dateOnly", srcAttr) && (
              <Form.Item name={f("dateOnly")} noStyle valuePropName="checked">
                <Checkbox>Показывать только дату</Checkbox>
              </Form.Item>
            )}
            {isFieldVisible("dateDefaultValueAbs", srcAttr) && (
              <Form.Item
                name={f("dateDefaultValueAbs")}
                label={labelDefault}
                tooltip={msgDefault}
              >
                <DatePickerString
                  placeholder="Дата"
                  showTime={!dateOnly}
                  allowClear
                />
              </Form.Item>
            )}

            {isFieldVisible("boolDefaultValue", srcAttr) && (
              <Form.Item
                name={f("boolDefaultValue")}
                label={labelDefault}
                tooltip={msgDefault}
              >
                <Radio.Group options={boolOptions} optionType="button" />
              </Form.Item>
            )}

            {isFieldVisible("maxCount", srcAttr) && (
              <div className={styles.col3}>
                <Form.Item
                  name={f("maxCount")}
                  label="Количество вхождений"
                  tooltip={msgMaxCount}
                >
                  <InputNumber min={1} max={100} precision={0} />
                </Form.Item>
                {isFieldVisible("maxSize", srcAttr) && (
                  <Form.Item
                    name={f("maxSize")}
                    label="Макс. размер файла"
                    tooltip={msgMaxSize}
                  >
                    <InputNumber min={0} addonAfter="МБ" />
                  </Form.Item>
                )}
              </div>
            )}

            {isFieldVisible("fileAccept", srcAttr) && (
              <Form.Item
                name={f("fileAccept")}
                label="Формат"
                tooltip={
                  <a
                    href="https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept"
                    target="_blank"
                    rel="noreferrer"
                  >
                    См. стандартное свойство HTML accept
                  </a>
                }
                rules={[
                  {
                    pattern: rxAccept,
                    message: "Неправильное описание формата",
                  },
                ]}
              >
                <Input allowClear />
              </Form.Item>
            )}

            {isFieldVisible("valuesCatalogComponent", srcAttr) && (
              <Form.Item
                name={f("valuesCatalogComponent")}
                label="Компонент"
                rules={[
                  { required: true, message: "Необходимо выбрать компонент" },
                ]}
              >
                <Select
                  options={(
                    [
                      "Select",
                      "SelectValuesModal",
                    ] satisfies ZAttrComponent["type"][]
                  ).map((value) => ({ value, label: value }))}
                  allowClear
                />
              </Form.Item>
            )}

            {isFieldVisible("valuesCatalogId", srcAttr) && (
              <Form.Item
                name={f("valuesCatalogId")}
                label="Каталог значений"
                rules={[{ required: true }]}
              >
                <SelectValueCatalogId />
              </Form.Item>
            )}
            {isFieldVisible("valueCatalogAttr", srcAttr) && (
              <Form.Item
                name={f("valueCatalogAttr")}
                label="Атрибут для отображения"
                rules={[{ required: true }]}
              >
                <SelectAttrForValueCatalog
                  initialValues={store.getInitialValues()}
                />
              </Form.Item>
            )}
            {isFieldVisible("linkToRecordId", srcAttr) && (
              <Form.Item
                name={f("linkToRecordId")}
                label="Каталог для ссылки"
                rules={[{ required: true }]}
              >
                <SelectCatalogId />
              </Form.Item>
            )}
            {isFieldVisible("inlineOptions", srcAttr) && <OptionsEditor />}

            {isFieldVisible("colSpan", srcAttr) && (
              <div className={styles.col3}>
                <Form.Item
                  name={f("colSpan")}
                  label="Колонки"
                  tooltip="Ширина для отображения в карточке: сколько колонок займёт"
                >
                  <Radio.Group
                    style={{ width: "100%" }}
                    defaultValue={1}
                    options={[1, 2, 3].map((value) => ({
                      value,
                      label: String(value),
                    }))}
                    optionType="button"
                  />
                </Form.Item>

                <Form.Item name={f("newLine")} label="С новой строки">
                  <Switch />
                </Form.Item>
              </div>
            )}
          </Form>
        )}
      </div>
    </MdmDrawer>
  );
});

const uniqueName = (usedNames: string[]): FormRule => ({
  validator: (_rule, value) => {
    if (value && usedNames.includes(value)) {
      return Promise.reject(Error("Такое название уже используется"));
    }
    return Promise.resolve();
  },
});

const checkMax: FormRule = (form) => ({
  validator: (_, maxVal) => {
    const minVal = form.getFieldValue(f("numberMin"));
    if (
      typeof maxVal === "number" &&
      typeof minVal === "number" &&
      maxVal < minVal
    ) {
      return Promise.reject(Error("Не должно быть меньше мин."));
    }
    return Promise.resolve();
  },
});

const testPattern: FormRule = {
  validator: async (_, value) => {
    try {
      if (value) {
        parseRegExp(value);
      }
    } catch (e) {
      throw Error(`Ошибка в регулярном выражении: ${cvtError(e).message}`);
    }
  },
};

const msgDefault =
  "При создании класса/записи атрибут будет заполняться указанным значением";
const msgOrder =
  "Задаёт приоритет для отображения атрибута в карточке. При одинаковых рангах порядок определяется порядком добавления. Сортировка по возрастанию.";
const patternExample =
  /^((8|\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d- ]{7,10}$/.toString();
const msgPattern = `Строковое представление регулярного выражения. Пример: ${patternExample}`;
const labelDefault = "Значение по умолчанию";
const msgMaxCount = "Определяет максимальное количество вхождений";
const msgMaxSize = "Ограничение допустимого размера каждого загружаемого файла";
